import {
  TellescopeModelFromName,
  useAuth,
  usePermission,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import {
  ActionButtonConfig,
  Button,
  cn,
  downloadCSV,
  useContainerWidth,
  useStateWithSetAction,
  VirtualizedColumnDef,
  VirtualizedDataTableProps,
} from "@tellescope/ui";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Download, Eye, Pencil, PlayCircle } from "lucide-react";
import { Link } from "../../Link";

export const useTicketQueuesPanel = () => {
  const { t } = useTranslation("ticket-queues");
  const { ref: containerRef, width: containerWidth } = useContainerWidth<HTMLDivElement>();
  const isCompact = containerWidth > 0 && containerWidth < 650;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [queueIdBeingEdited, setQueueIdBeingEdited] = useState<string | undefined>(undefined);
  const [isCreateEditFormOpen, setIsCreateEditFormOpen] = useStateWithSetAction(false, (newVal) => {
    if (!newVal) setQueueIdBeingEdited(undefined);
  });

  const ticketQueuesQuery = useTellescopeInfiniteQuery({
    model: "ticket_queues",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat().sort((a, b) => (a.index ?? Infinity) - (b.index ?? Infinity)),
  });

  const getNextTicketMutation = useTellescopeMutation({
    model: "tickets",
    method: "assign_from_queue",
    onSuccess: (res) => {
      navigate(`/tickets/details/${res.ticket.id}`);
    },
  });

  const updateTicketQueueIndexesMutation = useTellescopeMutation({
    model: "ticket_queues",
    method: "update_indexes",
  });

  const onReorder = useCallback(
    (reorderedItems: Array<{ id: string; index: number }>) => {
      updateTicketQueueIndexesMutation.mutateAsync({
        updates: reorderedItems,
      });
    },
    [updateTicketQueueIndexesMutation]
  );

  const { access: canUpdateTickets } = usePermission({
    model: "tickets",
    action: "update",
  });
  const { access: canCreateTicketQueues } = usePermission({
    model: "ticket_queues",
    action: "create",
  });
  const { access: canEditTicketQueues } = usePermission({
    model: "ticket_queues",
    action: "update",
  });

  const columns = useMemo<VirtualizedColumnDef<TellescopeModelFromName<"ticket_queues">>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        label: t("Title"),
        width: "2fr",
        minWidth: 150,
      },
      {
        id: "ticketCount",
        accessorKey: "count",
        label: isCompact ? t("Tickets") : t("Ticket Count"),
        width: "1fr",
        minWidth: 100,
        cell: ({ getValue }) => {
          let value = getValue();
          if (typeof value !== "number") value = 0;
          return <>{value}</>;
        },
      },
      {
        id: "userCount",
        accessorKey: "userIds",
        label: isCompact ? t("Users") : t("User Count"),
        width: "1fr",
        minWidth: 100,
        cell: ({ getValue }) => {
          let value = getValue();
          if (!Array.isArray(value)) value = 0;
          else value = value.length;
          return <>{value}</>;
        },
      },
      {
        id: "actions",
        label: "",
        width: isCompact ? 100 : 270,
        sortable: false,
        cell: ({ row }) => {
          const queue = row.original;
          const canGetNextTicket =
            canUpdateTickets && !queue.preventPull?.includes(currentUser.id) && !!row.original.count;

          return (
            <div
              className={cn("flex items-center gap-2 justify-end w-full", isCompact && "gap-0")}
              onClick={(e) => e.stopPropagation()}
            >
              {canGetNextTicket &&
                (isCompact ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    hoverLabel={t("Get Next Ticket")}
                    aria-label={t("Get Next Ticket")}
                    onClick={() => {
                      getNextTicketMutation.mutate({
                        userId: currentUser.id,
                        queueId: queue.id,
                      });
                    }}
                  >
                    <PlayCircle className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => {
                      getNextTicketMutation.mutate({
                        userId: currentUser.id,
                        queueId: queue.id,
                      });
                    }}
                  >
                    {t("Get Next Ticket")}
                  </Button>
                ))}
              <Link to={`/tickets/queues/${queue.id}`} aria-label={t("View Tickets")}>
                {isCompact ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    hoverLabel={t("View Tickets")}
                    aria-label={t("View Tickets")}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button>{t("View Tickets")}</Button>
                )}
              </Link>
              {canEditTicketQueues && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  hoverLabel={t("Edit Queue")}
                  aria-label={t("Edit Queue")}
                  onClick={() => {
                    setQueueIdBeingEdited(queue.id);
                    setIsCreateEditFormOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      canEditTicketQueues,
      canUpdateTickets,
      currentUser.id,
      getNextTicketMutation,
      isCompact,
      setIsCreateEditFormOpen,
      t,
    ]
  );

  const onDownload = useCallback(() => {
    const headerRow: string[] = [t("ID"), ...columns.slice(0, -1).map(({ label }) => label ?? "")];
    const dataRows: string[][] =
      ticketQueuesQuery.data?.map((queue) => [
        queue.id,
        queue.title,
        String(queue.count ?? 0),
        String(queue.userIds.length),
      ]) ?? [];

    downloadCSV({
      data: [headerRow, ...dataRows],
      filename: t("Ticket Queues Export - {{timestamp}}", { timestamp: new Date().toLocaleString() }),
    });
  }, [columns, t, ticketQueuesQuery.data]);

  const actionButtons = useMemo<ActionButtonConfig[]>(() => {
    const result: ActionButtonConfig[] = [
      {
        icon: Download,
        onClick: onDownload,
        label: "",
        hoverLabel: t("Export CSV"),
        variant: "ghost",
      },
    ];

    if (canCreateTicketQueues) {
      result.push({
        label: t("Create Queue"),
        onClick: () => setIsCreateEditFormOpen(true),
      });
    }

    return result;
  }, [canCreateTicketQueues, onDownload, setIsCreateEditFormOpen, t]);

  const virtualizedTableProps = useMemo(
    () =>
      ({
        data: ticketQueuesQuery.data ?? [],
        columns,
        getRowId: (row: TellescopeModelFromName<"ticket_queues">) => row.id,
        enableDragDrop: canEditTicketQueues,
        onReorder,
        actionButtons,
      }) satisfies Partial<VirtualizedDataTableProps<TellescopeModelFromName<"ticket_queues">>>,
    [actionButtons, canEditTicketQueues, columns, onReorder, ticketQueuesQuery.data]
  );

  return {
    containerRef,
    virtualizedTableProps,
    isCreateEditFormOpen,
    setIsCreateEditFormOpen,
    queueIdBeingEdited,
    setQueueIdBeingEdited,
    isLoading: ticketQueuesQuery.isLoading,
  };
};
